import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Clock, Users, Trash2, UserPlus, UserMinus, Calendar, HandHelping, Car, Pencil, Check, CheckCircle2, Clock4, Navigation } from "lucide-react";

import { pickColor } from "@/lib/palette";
import RideChat from "@/components/RideChat";
import { modeOf } from "@/components/ModePicker";

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

export default function RideDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [childName, setChildName] = useState("");
  const [acceptSeats, setAcceptSeats] = useState(3);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/rides/${id}`);
      setRide(data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isDriver = ride && user && ride.driver_id === user.id;
  const isRequester = ride && user && ride.requester_id === user.id;
  const isUnclaimedRequest = ride && ride.type === "request" && !ride.driver_id;
  const myBookings = ride ? ride.passengers.filter((p) => p.user_id === user.id) : [];

  const acceptRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${id}/accept`, { seats: acceptSeats });
      setRide(data);
      toast.success("Hai accettato la richiesta!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const book = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${id}/book`, { child_name: childName });
      setRide(data);
      setChildName("");
      toast.success(`Posto prenotato per ${data.passengers[data.passengers.length - 1].child_name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const unbook = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${id}/unbook`);
      setRide(data);
      toast.success("Prenotazione cancellata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmBooking = async (passenger) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${id}/confirm/${passenger.user_id}/${encodeURIComponent(passenger.child_name)}`);
      setRide(data);
      toast.success(`Prenotazione di ${passenger.child_name} confermata`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Sei sicuro di voler cancellare questa corsa?")) return;
    setBusy(true);
    try {
      await api.delete(`/rides/${id}`);
      toast.success("Corsa cancellata");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !ride) {
    return <div className="brut-card p-8 text-center font-bold max-w-2xl mx-auto">Caricamento…</div>;
  }

  const d = new Date(ride.date);
  const niceDate = `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;

  return (
    <div className="max-w-2xl mx-auto anim-slide-up space-y-5" data-testid="ride-detail-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 font-bold text-sm hover:underline" data-testid="ridedetail-back">
        <ArrowLeft className="w-4 h-4" /> Tutte le corse
      </Link>

      <div className="brut-card p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="brut-chip">
                <Calendar className="w-3 h-3" /> {niceDate}
              </span>
              {(() => {
                const m = modeOf(ride.mode);
                const ModeIcon = m.icon;
                return (
                  <span className="brut-chip" style={{ backgroundColor: `${m.color}20`, color: m.color }} data-testid="ridedetail-mode-badge">
                    <ModeIcon className="w-3 h-3" /> {m.label}
                  </span>
                );
              })()}
              {ride.type === "request" && (
                <span className={`brut-chip ${isUnclaimedRequest ? "bg-[#FFE9C2] text-[#FF9E1B]" : "bg-[#D4F1ED] text-[#26B5A8]"}`} data-testid="ridedetail-type-badge">
                  <HandHelping className="w-3 h-3" /> {isUnclaimedRequest ? "Richiesta di corsa" : "Richiesta accettata"}
                </span>
              )}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{ride.destination}</h1>
            {isUnclaimedRequest ? (
              <p className="text-[#8A91A8] mt-1">
                Richiesto da: <span className="font-semibold text-[#1A1D29]">{ride.requester_name}</span>
              </p>
            ) : (
              <p className="text-[#8A91A8] mt-1">
                Guidatore: <span className="font-semibold text-[#1A1D29]">{ride.driver_name || "—"}</span>
                {ride.type === "request" && ride.requester_name && (
                  <> · Richiedente: <span className="font-semibold text-[#1A1D29]">{ride.requester_name}</span></>
                )}
              </p>
            )}
          </div>
          {(isDriver || (isUnclaimedRequest && isRequester)) && (
            <div className="flex flex-col gap-2 shrink-0">
              {(isDriver || (isUnclaimedRequest && isRequester)) && (
                <Link to={`/rides/${id}/edit`} className="w-10 h-10 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#1A2A6C] hover:bg-[#E5E9F5] shadow-[0_2px_8px_rgba(20,32,80,0.04)]" title="Modifica" data-testid="ridedetail-edit-button">
                  <Pencil className="w-4 h-4" />
                </Link>
              )}
              <button onClick={remove} className="w-10 h-10 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#FF4D5E] hover:bg-[#FFE3E3] shadow-[0_2px_8px_rgba(20,32,80,0.04)]" title="Elimina" data-testid="ridedetail-delete-button">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-2xl bg-[#F5F6FA] p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Orario</p>
            <p className="font-heading text-2xl font-bold flex items-center gap-1 text-[#1A1D29]"><Clock className="w-5 h-5 text-[#1A2A6C]" /> {ride.time}</p>
          </div>
          {!isUnclaimedRequest && (
            <div className="rounded-2xl bg-[#F5F6FA] p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Posti</p>
              <p className="font-heading text-2xl font-bold flex items-center gap-1 text-[#1A1D29]"><Users className="w-5 h-5 text-[#1A2A6C]" /> {ride.seats_left}/{ride.seats}</p>
            </div>
          )}
          {ride.origin && (
            <div className="rounded-2xl bg-[#F5F6FA] p-4 col-span-2 sm:col-span-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Partenza</p>
              <p className="font-semibold text-sm flex items-center gap-1 truncate"><MapPin className="w-4 h-4 shrink-0 text-[#1A2A6C]" /> {ride.origin}</p>
            </div>
          )}
        </div>

        {/* External route via Google / Apple Maps */}
        {(() => {
          const fromQ = (ride.origin_address || ride.origin || "").trim();
          const toQ = (ride.destination_address || ride.destination || "").trim();
          if (!toQ) return null;
          const isiOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
          const gMode = ride.mode === "bike" ? "bicycling" : ride.mode === "walk" ? "walking" : "driving";
          const aMode = ride.mode === "walk" ? "w" : "d"; // Apple Maps: limited modes (drive/walk)
          const gURL = `https://www.google.com/maps/dir/?api=1${fromQ ? `&origin=${encodeURIComponent(fromQ)}` : ""}&destination=${encodeURIComponent(toQ)}&travelmode=${gMode}`;
          const aURL = `https://maps.apple.com/?${fromQ ? `saddr=${encodeURIComponent(fromQ)}&` : ""}daddr=${encodeURIComponent(toQ)}&dirflg=${aMode}`;
          return (
            <div className="rounded-2xl border border-[#ECEEF5] bg-[#F5F6FA] p-4 mb-5" data-testid="ride-route-actions">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 grid place-items-center rounded-xl bg-white text-[#1A2A6C] shrink-0 border border-[#ECEEF5]">
                  <Navigation className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-[#1A1D29] truncate">{ride.destination}</p>
                  <p className="text-xs text-[#8A91A8] truncate">{ride.destination_address || "Apri il percorso nella tua app preferita"}</p>
                </div>
              </div>
              <div className={`grid gap-2 ${isiOS ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2"}`}>
                <a
                  href={gURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brut-btn justify-center"
                  data-testid="ride-open-google-maps"
                >
                  <Navigation className="w-4 h-4" /> Apri in Google Maps
                </a>
                <a
                  href={aURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brut-btn-secondary justify-center inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#ECEEF5] font-bold text-[#1A2A6C] hover:bg-[#F5F6FA]"
                  data-testid="ride-open-apple-maps"
                >
                  <Navigation className="w-4 h-4" /> Apri in Apple Maps
                </a>
              </div>
            </div>
          );
        })()}

        {ride.notes && (
          <div className="rounded-2xl bg-[#FFF7E0] border border-[#FFE9A8] p-4 mb-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#B08D2A] mb-1">Note</p>
            <p className="font-medium text-[#1A1D29]">{ride.notes}</p>
          </div>
        )}

        {/* Accept request CTA */}
        {isUnclaimedRequest && !isRequester && (
          <div className="border-t border-[#ECEEF5] pt-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#FFE9C2] text-[#FF9E1B] shrink-0">
                <HandHelping className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold">Offri la corsa</h2>
                <p className="text-sm text-[#8A91A8]">Diventa il guidatore di questa richiesta. Indica quanti posti hai in auto.</p>
              </div>
            </div>
            <div>
              <label className="brut-label"><Users className="w-3 h-3 inline mr-1" /> Posti disponibili</label>
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <button type="button" key={n} onClick={() => setAcceptSeats(n)} className={`w-11 h-11 rounded-2xl font-heading font-bold text-lg transition-all ${acceptSeats === n ? "bg-[#FF9E1B] text-white shadow-[0_6px_18px_rgba(255,158,27,0.32)]" : "bg-white border border-[#ECEEF5] text-[#1A1D29] hover:bg-[#F5F6FA]"}`} data-testid={`ridedetail-accept-seats-${n}`}>{n}</button>
                ))}
              </div>
              <button onClick={acceptRequest} disabled={busy} className="brut-btn brut-btn-accent w-full" data-testid="ridedetail-accept-button">
                <Car className="w-4 h-4" /> Offrimi come guidatore
              </button>
            </div>
          </div>
        )}

        {isUnclaimedRequest && isRequester && (
          <div className="border-t border-[#ECEEF5] pt-5">
            <p className="text-sm text-[#8A91A8]">In attesa che un genitore si offra come guidatore. Riceverai una notifica appena qualcuno accetta.</p>
          </div>
        )}

        {/* Booking (offer or accepted request) */}
        {!isDriver && !isUnclaimedRequest && (
          <div className="border-t border-[#ECEEF5] pt-5">
            <h2 className="font-heading text-xl font-bold mb-3">Prenota un posto</h2>
            {ride.seats_left === 0 ? (
              <p className="brut-chip bg-[#FFE3E3] text-[#FF4D5E]">Posti esauriti</p>
            ) : (
              <form onSubmit={book} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  className="brut-input flex-1"
                  placeholder="Nome del bambino"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  required
                  maxLength={80}
                  data-testid="ridedetail-child-name-input"
                />
                <button type="submit" className="brut-btn brut-btn-secondary" disabled={busy} data-testid="ridedetail-book-button">
                  <UserPlus className="w-4 h-4" /> Prenota
                </button>
              </form>
            )}
            {myBookings.length > 0 && (
              <div className="mt-3">
                <button onClick={unbook} disabled={busy} className="brut-btn brut-btn-ghost text-sm" data-testid="ridedetail-unbook-button">
                  <UserMinus className="w-4 h-4" /> Cancella le mie prenotazioni
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Passengers */}
      <div className="brut-card p-6">
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#1A2A6C]" /> Passeggeri ({ride.passengers.length})
        </h2>
        {ride.passengers.length === 0 ? (
          <p className="text-[#8A91A8]">Nessuna prenotazione ancora.</p>
        ) : (
          <ul className="space-y-2" data-testid="ridedetail-passengers-list">
            {ride.passengers.map((p, i) => {
              const c = pickColor(p.user_id || i);
              const isMine = user && p.user_id === user.id;
              return (
              <li key={i} className="flex items-center justify-between gap-3 rounded-2xl bg-[#F5F6FA] p-3" data-testid={`passenger-row-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 grid place-items-center rounded-full font-heading font-bold shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                    {p.child_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold leading-tight truncate">{p.child_name}</p>
                    <p className="text-xs text-[#8A91A8] truncate">prenotato da {p.user_name}</p>
                    <div className="mt-1">
                      {p.confirmed ? (
                        <span className="brut-chip bg-[#D4F1ED] text-[#26B5A8]" data-testid={`passenger-confirmed-${i}`}>
                          <CheckCircle2 className="w-3 h-3" /> Confermata
                        </span>
                      ) : isDriver ? (
                        <span className="brut-chip bg-[#FFE9C2] text-[#B07512]" data-testid={`passenger-pending-driver-${i}`}>
                          <Clock4 className="w-3 h-3" /> Da confermare
                        </span>
                      ) : (
                        <span className="brut-chip bg-[#FFE9C2] text-[#B07512]" data-testid={`passenger-pending-${i}`}>
                          <Clock4 className="w-3 h-3" /> In attesa di conferma
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isDriver && !p.confirmed && (
                  <button
                    onClick={() => confirmBooking(p)}
                    disabled={busy}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#26B5A8] text-white text-xs font-bold hover:bg-[#1F9D91] active:translate-y-px shadow-[0_4px_12px_rgba(38,181,168,0.28)]"
                    data-testid={`confirm-passenger-${i}`}
                  >
                    <Check className="w-3.5 h-3.5" /> Conferma
                  </button>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Chat - mini chat between driver and booked passengers */}
      {!isUnclaimedRequest && (
        <RideChat rideId={id} currentUser={user} />
      )}
    </div>
  );
}
